import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useQuery, gql } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';

const GET_GUEST_RATINGS = gql`
  query GetGuestRatings($guestId: ID!, $limit: Int, $offset: Int) {
    getGuestRatings(guestId: $guestId, limit: $limit, offset: $offset) {
      summary {
        averageRating
        totalRatings
        ratingDistribution {
          fiveStars
          fourStars
          threeStars
          twoStars
          oneStar
        }
      }
      reviews {
        rating {
          id
          hostRating {
            general {
              score
            }
            comment
          }
          hostRatedAt
        }
        hostName
        hostAvatarUrl
        tripDate
        carName
      }
      totalCount
    }
  }
`;

const Container = styled.div`
  ${tw`mt-8`}
`;

const Header = styled.div`
  ${tw`mb-4`}
`;

const Title = styled.h2`
  ${tw`text-sm font-semibold text-text-secondary uppercase tracking-wide`}
`;

const TripCount = styled.span`
  ${tw`text-sm font-semibold text-text-secondary`}
`;

const SummaryRow = styled.div`
  ${tw`flex items-center gap-2 mb-6`}
`;

const RatingNumber = styled.span`
  ${tw`text-2xl font-bold`}
`;

const StarIcon = styled(FontAwesomeIcon)`
  color: #c9b896;
  ${tw`text-lg`}
`;

const ReviewCount = styled.span`
  ${tw`text-text-secondary text-sm`}
`;

const ReviewsContainer = styled.div``;

const ReviewCard = styled.div`
  ${tw`py-4 border-b border-border-default last:border-b-0`}
`;

const ReviewHeader = styled.div`
  ${tw`flex items-start gap-3`}
`;

const Avatar = styled.div`
  ${tw`w-12 h-12 rounded-full bg-bg-secondary flex items-center justify-center text-text-secondary font-medium overflow-hidden flex-shrink-0`}
`;

const ReviewMeta = styled.div`
  ${tw`flex-1`}
`;

const ReviewStars = styled.div`
  ${tw`flex gap-1 mb-1`}
`;

const ReviewerInfo = styled.div`
  ${tw`text-sm`}
`;

const ReviewerName = styled.span`
  ${tw`text-text-secondary font-medium`}
`;

const ReviewDate = styled.span`
  ${tw`text-text-tertiary ml-1`}
`;

const ReviewComment = styled.p`
  ${tw`text-text-secondary text-sm mt-3`}
`;

const RecommendText = styled.p`
  ${tw`text-sm text-text-secondary mt-2`}
`;

const SeeMoreButton = styled.button`
  ${tw`mt-6 px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-black transition-colors cursor-pointer`}
`;

const NoRatingsText = styled.p`
  ${tw`text-text-secondary py-4`}
`;

interface GuestRatingsSectionProps {
  guestId: string;
  tripCount?: number;
  initialLimit?: number;
}

export const GuestRatingsSection = ({
  guestId,
  tripCount = 0,
  initialLimit = 5,
}: GuestRatingsSectionProps) => {
  const [limit, setLimit] = useState(initialLimit);

  const { data, loading, error, fetchMore } = useQuery(GET_GUEST_RATINGS, {
    variables: { guestId, limit, offset: 0 },
    skip: !guestId,
  });

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>REVIEWS FROM HOSTS • {tripCount} TRIPS</Title>
        </Header>
        <NoRatingsText>Loading reviews...</NoRatingsText>
      </Container>
    );
  }

  if (error || !data?.getGuestRatings) {
    return null;
  }

  const { summary, reviews, totalCount } = data.getGuestRatings;

  if (summary.totalRatings === 0) {
    return (
      <Container>
        <Header>
          <Title>REVIEWS FROM HOSTS • {tripCount} TRIPS</Title>
        </Header>
        <NoRatingsText>No reviews yet</NoRatingsText>
      </Container>
    );
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSeeMore = () => {
    const newLimit = limit + 5;
    setLimit(newLimit);
    fetchMore({
      variables: { guestId, limit: newLimit, offset: 0 },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return fetchMoreResult;
      },
    });
  };

  return (
    <Container>
      <Header>
        <Title>REVIEWS FROM HOSTS • {tripCount} TRIPS</Title>
      </Header>

      <SummaryRow>
        <RatingNumber>{summary.averageRating.toFixed(1)}</RatingNumber>
        <StarIcon icon={faStar} />
        <ReviewCount>({summary.totalRatings} reviews)</ReviewCount>
      </SummaryRow>

      <ReviewsContainer>
        {reviews.map((review: any) => (
          <ReviewCard key={review.rating.id}>
            <ReviewHeader>
              <Avatar>
                {review.hostAvatarUrl ? (
                  <img
                    src={review.hostAvatarUrl}
                    alt={review.hostName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  getInitials(review.hostName || 'H')
                )}
              </Avatar>
              <ReviewMeta>
                <ReviewStars>
                  {[1, 2, 3, 4, 5].map(star => (
                    <FontAwesomeIcon
                      key={star}
                      icon={faStar}
                      style={{
                        color:
                          star <= (review.rating.hostRating?.general?.score || 0)
                            ? '#C9B896'
                            : '#E5E7EB',
                        fontSize: '16px',
                      }}
                    />
                  ))}
                </ReviewStars>
                <ReviewerInfo>
                  <ReviewerName>{review.hostName}</ReviewerName>
                  <ReviewDate>
                    •{' '}
                    {format(new Date(review.tripDate || review.rating.hostRatedAt), 'MMMM d, yyyy')}
                  </ReviewDate>
                </ReviewerInfo>
              </ReviewMeta>
            </ReviewHeader>

            {review.rating.hostRating?.general?.score === 5 && (
              <RecommendText>5/5 recommended</RecommendText>
            )}

            {review.rating.hostRating?.comment && (
              <ReviewComment>{review.rating.hostRating.comment}</ReviewComment>
            )}
          </ReviewCard>
        ))}

        {totalCount > reviews.length && (
          <SeeMoreButton onClick={handleSeeMore}>See more</SeeMoreButton>
        )}
      </ReviewsContainer>
    </Container>
  );
};
